import { useState } from 'react';
import { DeleteOutlined, NotificationOutlined, PlusOutlined } from '@ant-design/icons';
import { App, Button, DatePicker, Drawer, Empty, Form, Input, Space, Tag } from 'antd';
import dayjs from 'dayjs';
import { api } from '../api';
import { Bone, ErrorNotice, PageHeader, Panel, RowActions } from '../components';
import { formatDate, useLoad } from '../lib';
export function NotificationsPage() {
    const { message } = App.useApp();
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();
    const { data, error, loading, reload } = useLoad(() => api('/v1/admin/notifications').then((body) => body.notifications));
    const rows = data ?? [];
    const now = new Date().toISOString();
    async function publish(values) {
        setSaving(true);
        try {
            await api('/v1/admin/notifications', {
                method: 'POST',
                body: JSON.stringify({ ...values, publishedAt: values.publishedAt.format('YYYY-MM-DD') })
            });
            message.success(values.publishedAt.isAfter(dayjs(), 'day') ? 'Update scheduled' : 'Update published');
            setOpen(false);
            await reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not publish the update');
        }
        finally {
            setSaving(false);
        }
    }
    async function remove(row) {
        try {
            await api(`/v1/admin/notifications/${row.id}`, { method: 'DELETE' });
            message.success('Update removed');
            await reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not remove the update');
        }
    }
    return (<>
      <PageHeader title="Updates" description="Release notes shown in the desktop app from their publish date." actions={<Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
            New update
          </Button>}/>
      <ErrorNotice error={error}/>

      <Panel flush title="Published and scheduled" description={loading && !data ? <Bone width={70} height={11} style={{ marginTop: 4 }}/> : `${rows.length} ${rows.length === 1 ? 'update' : 'updates'}`}>
        {loading && !data ? (<ul className="feed" style={{ padding: 0 }} aria-busy>
            {Array.from({ length: 3 }, (_, index) => (<li key={index} style={{ padding: '18px 20px', borderBottom: '1px solid var(--line)', alignItems: 'flex-start' }}>
                <Bone width={36} height={36} round/>
                <div className="bone-lines">
                  <Bone width={index % 2 ? 180 : 240} height={13}/>
                  <Bone width="92%" height={10}/>
                  <Bone width="76%" height={10}/>
                  <Bone width={110} height={10}/>
                </div>
                <Bone width={28} height={28} style={{ borderRadius: 8 }}/>
              </li>))}
          </ul>) : rows.length === 0 ? (<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No updates yet" style={{ padding: 32 }}>
            <Button onClick={() => setOpen(true)}>Write the first update</Button>
          </Empty>) : (<ul className="feed" style={{ padding: 0 }}>
            {rows.map((row) => {
                const scheduled = row.publishedAt > now;
                return (<li key={row.id} style={{ padding: '18px 20px', borderBottom: '1px solid var(--line)', alignItems: 'flex-start' }}>
                  <span className="feed-icon tone-sky" style={{ width: 36, height: 36 }}>
                    <NotificationOutlined />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <strong style={{ fontWeight: 600 }}>{row.title}</strong>
                      {row.version && <Tag variant="filled" className="mono">v{row.version}</Tag>}
                      {scheduled ? <Tag color="processing" variant="filled">Scheduled</Tag> : <Tag color="success" variant="filled">Live</Tag>}
                    </div>
                    <p style={{ margin: '6px 0 4px', color: 'var(--body)', whiteSpace: 'pre-line' }}>{row.body}</p>
                    <span className="cell-sub">{scheduled ? 'Goes live' : 'Published'} {formatDate(row.publishedAt)}</span>
                  </div>
                  <RowActions label={`Actions for ${row.title}`} actions={[
                        {
                            key: 'remove',
                            icon: <DeleteOutlined />,
                            label: 'Remove update',
                            danger: true,
                            hint: 'It disappears from the desktop app',
                            confirm: { title: 'Remove this update?', description: `"${row.title}" disappears from the desktop app.`, okText: 'Remove' },
                            onClick: () => remove(row)
                        }
                    ]}/>
                </li>);
            })}
          </ul>)}
      </Panel>

      <Drawer title="New update" open={open} onClose={() => setOpen(false)} size={520} destroyOnHidden footer={<Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={() => form.submit()}>
              Publish
            </Button>
          </Space>}>
        <Form form={form} layout="vertical" requiredMark={false} onFinish={publish} initialValues={{ publishedAt: dayjs() }}>
          <Form.Item label="Title" name="title" rules={[{ required: true, message: 'Enter a title.' }]}>
            <Input placeholder="Faster bank statement import" autoFocus maxLength={200}/>
          </Form.Item>
          <Form.Item label="Message" name="body" rules={[{ required: true, message: 'Write the message.' }]}>
            <Input.TextArea rows={6} maxLength={4000} showCount placeholder="What changed, and what customers should do."/>
          </Form.Item>
          <div className="form-row">
            <Form.Item label="Version" name="version">
              <Input placeholder="0.2.0"/>
            </Form.Item>
            <Form.Item label="Publish on" name="publishedAt" rules={[{ required: true }]} extra="A later date schedules it.">
              <DatePicker placeholder="Select date" style={{ width: '100%' }} format="DD MMM YYYY" allowClear={false}/>
            </Form.Item>
          </div>
        </Form>
      </Drawer>
    </>);
}
